<script setup lang="ts">
    import { computed } from 'vue';
    import CloseIcon from '~/components/icons/CloseIcon.vue';

    const props = defineProps<{ blok: PromotionTeaserStoryblok }>();
    const { t } = useI18n();
    const now = useState<number>('now', () => Date.now());

    function parseStoryblokDateFieldUTC(input: unknown): Date | null {
        if (!input || typeof input !== 'string') return null;
        const trimmed = input.trim();
        if (!trimmed) return null;

        const trimmedDate = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})$/.exec(trimmed);
        if (!trimmedDate) return null;

        const [, year, month, day, hour, minute] = trimmedDate;
        if (!year || !month || !day || !hour || !minute) return null;
        const utcDate = Date.UTC(+year, +month - 1, +day, +hour, +minute, 0, 0);
        const dateTime = new Date(utcDate);
        return Number.isNaN(dateTime.getTime()) ? null : dateTime;
    }

    const startDate = computed(() => parseStoryblokDateFieldUTC(props.blok.start));
    const endDate = computed(() => parseStoryblokDateFieldUTC(props.blok.end));

    const isWithinSchedule = computed(() => {
        const start = startDate.value?.getTime();
        const end = endDate.value?.getTime();

        if (!start && !end) return true;
        if (start && !end) return now.value >= start;
        if (!start && end) return now.value <= end;
        return now.value >= start! && now.value <= end!;
    });

    const cookieKey = `hidePromotionTeaser_${props.blok._uid}`;

    const hidePromotionTeaser = useCookie<boolean>(cookieKey, {
        default: () => false,
        maxAge: 60 * 60 * 24 * 60,
        path: '/'
    });

    const dismissTeaser = () => {
        hidePromotionTeaser.value = true;
    };
</script>

<template>
    <div
        v-if="isWithinSchedule && !hidePromotionTeaser"
        v-editable="props.blok"
        class="promotion-teaser"
        :class="`layout--${props.blok.layout}`"
    >
        <div class="promotion-teaser-inner">
            <ATextComponent
                v-for="text in props.blok.text"
                :key="text._uid"
                :blok="text"
            />

            <button
                v-if="!(props.blok as any).noCloseOption"
                class="promotion-teaser-close"
                :aria-label="t('general.close')"
                @click="dismissTeaser()"
            >
                <CloseIcon />
            </button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/navigation/promotion-teaser.scss';
    @import '~/assets/scss/text/text.scss';
</style>
